/**
 * DataTab — the source's collected content as a reader: the list of items on the left, the selected item and its
 * analysis in the reading pane on the right.
 * Demo page: `/email` (inbox-style list + reader), built on `SplitPane`.
 * Fetched content is external data: the body is plain text (never markup) and every link goes through `safeHref`.
 */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, Inbox, Zap } from 'lucide-react';
import {
  fetchIntelSourceMutation,
  getIntelSourceHarvestedOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { IntelSourceData } from '../../../types/intel-source.js';
import { Can } from '../../../components/auth/Can.js';
import {
  Badge,
  Button,
  Callout,
  Card,
  Chip,
  EmptyState,
  FilterPills,
  SearchBar,
  Skeleton,
  SplitPane,
  useToast,
} from '../../../components/ui';
import { safeHref } from '../../../lib/safe-url.js';
import { cn } from '../../../utils/cn.js';
import { ItemAnalysis, readMediaAnalysis } from './ItemAnalysis.js';
import { useHarvestedInvalidation } from './intel-source-queries.js';
import { formatRelativeTime } from './SourceCard.js';

interface DataTabProps {
  sourceId: number;
}

type ContentFilter = 'all' | 'processed' | 'bullish' | 'bearish';

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'processed', label: 'Processed' },
  { id: 'bullish', label: 'Bullish' },
  { id: 'bearish', label: 'Bearish' },
];

const QUIET_PERIOD_NOTE =
  'Fetching is held back for a few minutes after the engine starts, so a source can look empty right after a restart.';

function sentimentVariant(sentiment: string): 'success' | 'danger' | 'neutral' {
  const s = sentiment.toLowerCase();
  if (s.includes('bull')) return 'success';
  if (s.includes('bear')) return 'danger';
  return 'neutral';
}

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** The first line of an item's text, used when the item has no title. */
function headline(item: IntelSourceData): string {
  if (item.title) return item.title;
  const firstLine = (item.body ?? '').trim().split('\n')[0] ?? '';
  return firstLine.length > 0 ? firstLine : 'Untitled item';
}

function applyFilter(items: IntelSourceData[], filter: ContentFilter, search: string): IntelSourceData[] {
  let result = items;
  if (filter === 'processed') result = result.filter((i) => i.processed);
  else if (filter === 'bullish') result = result.filter((i) => i.sentiment?.toLowerCase().includes('bull'));
  else if (filter === 'bearish') result = result.filter((i) => i.sentiment?.toLowerCase().includes('bear'));

  const q = search.trim().toLowerCase();
  if (q) {
    result = result.filter(
      (i) =>
        i.title?.toLowerCase().includes(q) ||
        i.body?.toLowerCase().includes(q) ||
        i.asset?.toLowerCase().includes(q) ||
        i.sentiment?.toLowerCase().includes(q),
    );
  }
  return result;
}

// ── List pane ───────────────────────────────────────────────────────────────────

function ItemRow({ item, selected, onSelect }: { item: IntelSourceData; selected: boolean; onSelect: () => void }) {
  return (
    <Button
      variant="ghost"
      aria-current={selected ? 'true' : undefined}
      onClick={onSelect}
      className={cn(
        'h-auto w-full flex-col items-stretch gap-1 whitespace-normal rounded-lg px-3 py-3 text-left font-normal',
        selected && 'bg-surface-alt',
      )}
    >
      <span className="flex items-start justify-between gap-3">
        <span className="line-clamp-1 text-sm font-medium text-heading">{headline(item)}</span>
        <span className="shrink-0 text-xs text-muted">{formatRelativeTime(item.occurredAt)}</span>
      </span>
      {item.body && item.title && <span className="line-clamp-2 text-xs text-muted">{item.body}</span>}
      <span className="flex flex-wrap items-center gap-1.5">
        {item.sentiment != null && (
          <Badge variant={sentimentVariant(item.sentiment)} size="sm">
            {sentenceCase(item.sentiment)}
          </Badge>
        )}
        {item.asset != null && <Chip tone="neutral">{item.asset}</Chip>}
        {!item.processed && (
          <Badge variant="warning" dot size="sm">
            Queued
          </Badge>
        )}
      </span>
    </Button>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-4 p-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton height={12} width="70%" />
          <Skeleton height={10} width="90%" />
          <Skeleton height={16} width={80} rounded="rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ── Reading pane ───────────────────────────────────────────────────────────────

function ReadingPane({ item, sourceId }: { item: IntelSourceData; sourceId: number }) {
  const analyzed = readMediaAnalysis(item)?.status === 'completed';
  const original = safeHref(item.sourceUrl);

  return (
    <Card className="min-h-full">
      <article className="space-y-5">
        <header className="space-y-2">
          <h3 className="text-lg font-semibold text-heading">{headline(item)}</h3>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <time dateTime={item.occurredAt}>{new Date(item.occurredAt).toLocaleString()}</time>
            {item.sentiment != null && (
              <Badge variant={sentimentVariant(item.sentiment)} size="sm">
                {sentenceCase(item.sentiment)}
              </Badge>
            )}
            {item.asset != null && <Chip tone="neutral">{item.asset}</Chip>}
            <Badge variant={item.processed ? 'success' : 'warning'} dot size="sm">
              {item.processed ? 'Processed' : 'Queued'}
            </Badge>
            {analyzed && (
              <Badge variant="accent" size="sm">
                Analyzed
              </Badge>
            )}
            {original && (
              <a
                href={safeHref(item.sourceUrl) ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-accent-text hover:underline"
              >
                Open original <ExternalLink size={12} aria-hidden="true" />
              </a>
            )}
          </div>
        </header>

        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-body">
          {item.body ?? <span className="text-faint">This item has no text.</span>}
        </div>

        <section aria-label="Analysis" className="border-t border-border pt-5">
          <h4 className="mb-3 text-sm font-medium text-heading">Analysis</h4>
          <ItemAnalysis sourceId={sourceId} item={item} />
        </section>
      </article>
    </Card>
  );
}

// ── Tab ────────────────────────────────────────────────────────────────────────

export const DataTab: React.FC<DataTabProps> = ({ sourceId }) => {
  const [filter, setFilter] = useState<ContentFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const invalidate = useHarvestedInvalidation();
  const { notify } = useToast();

  const { data, isLoading, isError, refetch } = useQuery({
    ...getIntelSourceHarvestedOptions({ path: { id: sourceId }, query: { limit: 100 } }),
    staleTime: 60_000,
  });

  const fetchMutation = useMutation({
    ...fetchIntelSourceMutation(),
    onSuccess: () => {
      invalidate(sourceId);
      notify('Fetch queued.');
    },
    onError: () => notify('Could not queue a fetch.', 'danger'),
  });
  const fetchNow = () => fetchMutation.mutate({ path: { id: sourceId }, body: {} });

  const items = data?.data ?? [];
  const filtered = applyFilter(items, filter, search);
  const selected = filtered.find((i) => i.id === selectedId) ?? filtered[0] ?? null;

  if (isError) {
    return (
      <Callout
        tone="danger"
        title="Could not load the collected content"
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Callout>
    );
  }

  const fetchButton = (
    <Can policy="CanManageAgents">
      <Button variant="secondary" size="sm" loading={fetchMutation.isPending} onClick={fetchNow}>
        <Zap size={14} aria-hidden="true" /> Fetch now
      </Button>
    </Can>
  );

  if (!isLoading && items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Inbox}
          title="No content collected yet"
          description={`Nothing has been collected from this source. ${QUIET_PERIOD_NOTE}`}
          action={fetchButton}
        />
      </Card>
    );
  }

  const list = (
    <Card className="flex h-full min-h-[560px] flex-col p-0">
      <div className="space-y-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SearchBar value={search} onChange={setSearch} placeholder="Search content…" />
          </div>
          {fetchButton}
        </div>
        <FilterPills
          aria-label="Content filter"
          options={FILTER_OPTIONS}
          value={filter}
          onChange={(id) => setFilter(id as ContentFilter)}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2" role="list" aria-label="Collected content">
        {isLoading ? (
          <ListSkeleton />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <Inbox size={22} className="text-faint" aria-hidden="true" />
            <p className="text-sm text-muted">No items match the filter.</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilter('all');
                setSearch('');
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          filtered.map((item) => (
            <div key={item.id} role="listitem">
              <ItemRow item={item} selected={selected?.id === item.id} onSelect={() => setSelectedId(item.id)} />
            </div>
          ))
        )}
      </div>
    </Card>
  );

  const reader = selected ? (
    <ReadingPane item={selected} sourceId={sourceId} />
  ) : (
    <Card className="min-h-[560px]">
      <EmptyState
        icon={Inbox}
        title="Select an item"
        description="Pick an item from the list to read it and see its analysis."
      />
    </Card>
  );

  return (
    <SplitPane
      left={list}
      right={reader}
      storageKey="intel-source-reader"
      defaultLeft={38}
      minLeft={28}
      maxLeft={60}
      handleLabel="Resize the list and reading pane"
    />
  );
};
